import {
  createStudentApi,
  mapStudentRankingToPanelData,
  useAuthContext,
} from '@e-learning/core';
import type {
  StudentRankingCurrentUser,
  StudentRankingEntry,
} from '@e-learning/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';

const studentApi = createStudentApi(api);

export interface UseStudentRankingResult {
  entries: StudentRankingEntry[];
  currentUser: StudentRankingCurrentUser | undefined;
  loading: boolean;
  error: { key: 'student_dashboard.ranking_load_error' } | null;
  refetch: () => void;
}

/**
 * Carrega o ranking do aluno (GET v1/student/ranking) para o painel do dashboard.
 */
export function useStudentRanking(): UseStudentRankingResult {
  const { i18n } = useTranslation();
  const { user } = useAuthContext();
  const [entries, setEntries] = useState<StudentRankingEntry[]>([]);
  const [currentUser, setCurrentUser] = useState<
    StudentRankingCurrentUser | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<
    { key: 'student_dashboard.ranking_load_error' } | null
  >(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    studentApi
      .fetchRanking()
      .then(data => {
        if (cancelled) return;
        const mapped = mapStudentRankingToPanelData(data);
        const fallbackCurrentUser =
          mapped.currentUser ??
          (user?.userId
            ? (() => {
                const selfFromTop = data.top.find(
                  row => row.userId === user.userId
                );
                if (!selfFromTop) return undefined;
                return {
                  position: selfFromTop.rank,
                  label: selfFromTop.name.trim(),
                  xp: selfFromTop.activePoints,
                } satisfies StudentRankingCurrentUser;
              })()
            : undefined);
        const filteredEntries =
          user?.userId && fallbackCurrentUser
            ? mapped.entries.filter(entry => entry.id !== user.userId)
            : mapped.entries;
        setEntries(filteredEntries);
        setCurrentUser(fallbackCurrentUser);
      })
      .catch(() => {
        if (cancelled) return;
        // Mantemos uma chave i18n para garantir que a mensagem acompanhe a troca de idioma.
        setError({ key: 'student_dashboard.ranking_load_error' });
        setEntries([]);
        setCurrentUser(undefined);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tick, user?.userId, i18n.language]);

  const refetch = useCallback(() => setTick(n => n + 1), []);

  return {
    entries,
    currentUser,
    loading,
    error,
    refetch,
  };
}
